import { AddedOrganisationAssignment, AddOrganisationSelection } from "../../../../../models";
import { ServiceError } from "../../../../../service/Shared";
import { ExistingDeveloperPrompt, findDuplicateProjectKeys } from "./accessAssignments";

/**
 * What the add-organisation card refuses before anything is staged: an incomplete selection, a
 * repeat of an (organisation, project) pair the api would reject for the whole batch, or a
 * Developer swap that has not said what becomes of the developer it displaces (BR-02873).
 *
 * Each error names the card field it belongs to in `property`, so the card can show it in place.
 */
export const validateAddSelection = (
  selection: AddOrganisationSelection,
  organisations: AddedOrganisationAssignment[],
  existingDeveloperPrompts: ExistingDeveloperPrompt[],
  projectNameByKey: Record<string, string>
): ServiceError[] => {
  const errors: ServiceError[] = [];

  if (!selection.organisationUuid) {
    errors.push({
      code: "VALIDATION_ATTRIBUTE_REQUIRED",
      message: "Select an organisation",
      property: "organisationUuid",
    });
  }
  if (!selection.access) {
    errors.push({ code: "VALIDATION_ATTRIBUTE_REQUIRED", message: "Select an access level", property: "access" });
  }
  if (selection.projectKeys.length === 0) {
    errors.push({
      code: "VALIDATION_ATTRIBUTE_REQUIRED",
      message: "Select at least one project",
      property: "projectKeys",
    });
  }

  // The remaining checks assume a complete selection, so they wait until it is one.
  if (errors.length > 0) return errors;

  const duplicates = findDuplicateProjectKeys(
    organisations,
    selection.organisationUuid as string,
    selection.projectKeys
  );

  if (duplicates.length > 0) {
    return [
      {
        code: "VALIDATION_ATTRIBUTE_INVALID",
        property: "projectKeys",
        message: `That organisation has already been added to ${duplicates
          .map((projectKey) => projectNameByKey[projectKey] ?? projectKey)
          .join(", ")}.`,
      },
    ];
  }

  const unanswered = existingDeveloperPrompts.filter(
    (prompt) => !selection.existingDeveloperActions?.[prompt.projectKey]
  );

  if (unanswered.length > 0) {
    return [
      {
        code: "VALIDATION_INVALID_EXISTING_DEVELOPER_ACTION",
        property: "existingDeveloperActions",
        message: `Choose what happens to the current developer on ${unanswered
          .map((prompt) => projectNameByKey[prompt.projectKey] ?? prompt.projectKey)
          .join(", ")}.`,
      },
    ];
  }

  return [];
};
